/**
 * chatArtifactProgress — /chat 파일 생성(file-artifact) 진행 상태를 메시지 하나의
 * "진행형 파일 카드"로 묶기 위한 순수 함수 모음.
 *
 * 배경: 서버는 (1) 본문 텍스트를 스트리밍한 뒤 (2) 같은 본문을 실제 파일로 렌더한다.
 * 예전 UI는 (1)에서 스피너를 끄고 (2)에서 다시 켜서 "두 번 기다리는" 느낌을 줬다.
 * 이 모듈은 첫 신호부터 완료까지 `message.pendingArtifact` 하나를 유지하도록 상태 전이를 계산한다.
 *
 * 서버 신호(순서):
 *   status  file_artifact_planned     — 파일을 만들 예정 (본문 시작 전/직후, 선택적)
 *   content (첫 줄이 대기 문장)        — 구형 백엔드 호환용 fallback 신호
 *   status  file_artifact_generating  — 본문 끝, 렌더 시작 (artifacts_planned 포함 가능)
 *   file_artifact_result              — 파일 1개 완료/실패
 *   complete                          — 턴 종료 (artifacts[] 최종 목록)
 *
 * 모든 함수는 입력을 변경하지 않는다.
 */

export const FILE_GENERATION_WAIT_NOTICE =
  '답변을 마치면 파일로 내려받을 수 있게 준비해드릴게요. 잠시만 기다려주세요.';

export const ARTIFACT_STAGE = Object.freeze({
  WRITING: 'writing',
  RENDERING: 'rendering',
});

export const ARTIFACT_FORMAT_LABELS = Object.freeze({
  xlsx: '엑셀',
  csv: 'CSV',
  hwpx: '한글',
  hwp: '한글',
  docx: '워드',
  pdf: 'PDF',
});

// 모델이 띄어쓰기·마침표를 조금 다르게 써도 잡히도록 느슨하게 매칭한다.
const WAIT_NOTICE_RE =
  /답변을\s*마치면\s*파일로\s*내려\s*받을\s*수\s*있게\s*준비해\s*드릴게요\.?\s*(?:잠시만\s*기다려\s*주세요\.?)?/g;

const normalizeForPrefix = (value) => String(value || '').replace(/\s+/g, '');

const NOTICE_NORMALIZED = normalizeForPrefix(FILE_GENERATION_WAIT_NOTICE);

/**
 * 스트리밍 중 대기 문장이 아직 다 도착하지 않았을 때(예: "답변을 마치면 파일로")
 * 문장 조각만 있는지 판정한다. 조각이면 화면에 잠깐 보였다 사라지는 깜빡임을 막기 위해 숨긴다.
 */
export const isWaitNoticePrefix = (text) => {
  const normalized = normalizeForPrefix(text);
  if (!normalized) return false;
  if (normalized.length >= NOTICE_NORMALIZED.length) return false;
  return NOTICE_NORMALIZED.startsWith(normalized);
};

/** 대기 문장이 (완성된 형태로) 포함되어 있는지 */
export const hasFileGenerationWaitNotice = (text) => {
  WAIT_NOTICE_RE.lastIndex = 0;
  return WAIT_NOTICE_RE.test(String(text || ''));
};

/**
 * 화면 표시용 텍스트에서 대기 문장을 제거한다.
 * - 완성된 문장은 어디에 있든 제거 (구형 백엔드가 저장한 과거 메시지 호환)
 * - 텍스트 전체가 문장의 앞부분 조각이면 빈 문자열
 */
export const stripFileGenerationWaitNotice = (text) => {
  const source = String(text || '');
  if (!source) return '';
  if (isWaitNoticePrefix(source.trim())) return '';
  WAIT_NOTICE_RE.lastIndex = 0;
  const stripped = source.replace(WAIT_NOTICE_RE, '');
  if (stripped === source) return source;
  return stripped.replace(/^\s+/, '').replace(/\n{3,}/g, '\n\n');
};

const normalizePlanned = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const format = String(item.format || '').trim().toLowerCase();
      const filename = String(item.filename || '').trim();
      if (!format && !filename) return null;
      return { format, filename };
    })
    .filter(Boolean);
};

/**
 * pendingArtifact 객체를 만들거나 갱신한다.
 * stage는 뒤로 돌아가지 않는다 (rendering → writing 금지).
 */
export const buildPendingArtifact = (previous, { stage, planned } = {}) => {
  const prev = previous && typeof previous === 'object' ? previous : null;
  const nextPlanned = normalizePlanned(planned);
  const prevStage = prev?.stage || null;
  let nextStage = stage || prevStage || ARTIFACT_STAGE.WRITING;
  if (prevStage === ARTIFACT_STAGE.RENDERING) {
    nextStage = ARTIFACT_STAGE.RENDERING;
  }
  const mergedPlanned = nextPlanned.length > 0 ? nextPlanned : (prev?.planned || []);
  return {
    stage: nextStage,
    planned: mergedPlanned,
    // 서버가 몇 개를 만들지 알려준 경우, 결과가 그만큼 오면 카드를 내린다.
    expected: mergedPlanned.length > 0 ? mergedPlanned.length : (prev?.expected || 0),
    received: prev?.received || 0,
  };
};

const isArtifactStatusType = (statusType) =>
  statusType === 'file_artifact_planned' || statusType === 'file_artifact_generating';

/** 이 청크가 파일 생성 진행 카드에 영향을 주는 청크인지 (메시지 상태와 무관하게 판정) */
export const isArtifactProgressChunk = (chunkData) => {
  if (!chunkData || typeof chunkData !== 'object') return false;
  if (chunkData.type === 'file_artifact_result') return true;
  return chunkData.type === 'status' && isArtifactStatusType(chunkData.statusType);
};

/**
 * 스트림 청크 하나를 보고 메시지에 반영할 부분 업데이트를 돌려준다.
 * 파일과 무관한 청크면 null.
 *
 * @param {object} message 현재 assistant 메시지
 * @param {object} chunkData chatApi.onChunk 가 넘기는 청크 (status는 camelCase: statusType, artifactsPlanned)
 */
export const applyArtifactChunk = (message, chunkData) => {
  if (!chunkData || typeof chunkData !== 'object') return null;
  const msg = message || {};

  if (chunkData.type === 'status' && isArtifactStatusType(chunkData.statusType)) {
    const planned = chunkData.artifactsPlanned || chunkData.artifacts_planned;
    if (chunkData.statusType === 'file_artifact_planned') {
      // 본문 시작 전에 오는 경우(사용자가 형식을 명시) 상단 "답변을 준비하고 있어요"와
      // 카드가 동시에 보이지 않도록, 이 시점부터는 카드가 로딩 표시를 맡는다.
      return {
        pendingArtifact: buildPendingArtifact(msg.pendingArtifact, {
          stage: ARTIFACT_STAGE.WRITING,
          planned,
        }),
        currentStatus: null,
        isSearching: false,
        streaming: true,
      };
    }
    // file_artifact_generating: 본문은 끝났고 파일로 굳히는 단계.
    // 상단 ThinkingIndicator를 다시 켜지 않고 카드 문구만 바꾼다.
    return {
      pendingArtifact: buildPendingArtifact(msg.pendingArtifact, {
        stage: ARTIFACT_STAGE.RENDERING,
        planned,
      }),
      currentStatus: null,
      isSearching: false,
      streaming: true,
    };
  }

  if (chunkData.type === 'file_artifact_result') {
    const pending = msg.pendingArtifact || null;
    if (chunkData.success && chunkData.artifact?.file_id) {
      const existing = Array.isArray(msg.artifacts) ? msg.artifacts : [];
      const already = existing.some((item) => item?.file_id === chunkData.artifact.file_id);
      const artifacts = already ? existing : [...existing, chunkData.artifact];
      const received = (pending?.received || 0) + 1;
      const expected = pending?.expected || 0;
      const stillPending = expected > 0 && received < expected;
      return {
        artifacts,
        pendingArtifact: stillPending ? { ...pending, received } : null,
      };
    }
    // 실패: 카드는 내리고, 안내 토스트는 기존 ChatPage 핸들러가 담당한다.
    return { pendingArtifact: null };
  }

  return null;
};

/**
 * content 청크 fallback: 서버가 file_artifact_planned 를 보내지 않는 구형 배포에서도
 * 첫 줄 대기 문장으로 "파일 예정"을 감지한다. 이미 pending이 있으면 null.
 */
export const detectPendingArtifactFromContent = (message, content) => {
  if (message?.pendingArtifact) return null;
  const head = String(content || '').trimStart().slice(0, 160);
  if (!head) return null;
  if (hasFileGenerationWaitNotice(head) || isWaitNoticePrefix(head)) {
    return {
      pendingArtifact: buildPendingArtifact(null, { stage: ARTIFACT_STAGE.WRITING }),
    };
  }
  return null;
};

/**
 * 카드에 표시할 문구.
 * @returns {{ title: string, status: string, format: string }}
 */
export const describePendingArtifact = (pending, { contentLength = 0 } = {}) => {
  const first = pending?.planned?.[0] || null;
  const format = first?.format || '';
  const formatLabel = ARTIFACT_FORMAT_LABELS[format] || (format ? format.toUpperCase() : '');
  // 서버 filename 은 보통 확장자를 이미 포함한다(sanitize_filename). 없을 때만 붙인다.
  const filename = first?.filename || '';
  const hasExtension = format && filename.toLowerCase().endsWith(`.${format}`);
  const title = filename
    ? (format && !hasExtension ? `${filename}.${format}` : filename)
    : (formatLabel ? `${formatLabel} 문서` : '파일');

  if (pending?.stage === ARTIFACT_STAGE.RENDERING) {
    return { title, status: '파일로 변환 중', format };
  }
  const base = formatLabel ? `${formatLabel} 문서 작성 중` : '내용 작성 중';
  const count = Number(contentLength) > 0
    ? ` · ${Math.round(Number(contentLength)).toLocaleString('ko-KR')}자`
    : '';
  return { title, status: `${base}${count}`, format };
};
